import { matrix } from './Matrix';

const STRAIGHT_STEERING_LEVEL = 0;
const DEFAULT_MAX_STEERING_LEVEL = 8;
const DEFAULT_TURN_STEP_DEGREES = 5;
const DEFAULT_TURN_RIGHT_RADIANS = DEFAULT_TURN_STEP_DEGREES * Math.PI / 180;
const DEFAULT_TURN_LEFT_RADIANS = -DEFAULT_TURN_RIGHT_RADIANS;

function createTurnMatrix(turnInRadians: number): matrix {
    const result = new matrix(3, 3);
    result.setValues([
        Math.cos(turnInRadians), -Math.sin(turnInRadians), 0,
        Math.sin(turnInRadians), Math.cos(turnInRadians), 0,
        0, 0, 1
    ]);
    return result;
}

function createSteeringTurnMatrices(turnRightInRadians: number, turnLeftInRadians: number): { downMat: matrix; upMat: matrix } {
    return {
        downMat: createTurnMatrix(turnRightInRadians),
        upMat: createTurnMatrix(turnLeftInRadians)
    };
}

function createDefaultSteeringTurnMatrices(): { downMat: matrix; upMat: matrix } {
    return createSteeringTurnMatrices(DEFAULT_TURN_RIGHT_RADIANS, DEFAULT_TURN_LEFT_RADIANS);
}

export class SteeringState {
    private level: number;
    private readonly maxLevel: number;
    private readonly downMat: matrix;
    private readonly upMat: matrix;
    private pendingTurnMatrix: matrix | undefined;

    constructor(maxLevel: number = DEFAULT_MAX_STEERING_LEVEL, initialLevel: number = STRAIGHT_STEERING_LEVEL) {
        this.maxLevel = maxLevel;
        this.level = Math.max(-this.maxLevel, Math.min(this.maxLevel, initialLevel));
        const { downMat, upMat } = createDefaultSteeringTurnMatrices();
        this.downMat = downMat;
        this.upMat = upMat;
        this.pendingTurnMatrix = undefined;
    }

    get Level(): number {
        return this.level;
    }

    isStraight(): boolean {
        return this.level === STRAIGHT_STEERING_LEVEL;
    }

    isLeft(): boolean {
        return this.level < STRAIGHT_STEERING_LEVEL;
    }

    isRight(): boolean {
        return this.level > STRAIGHT_STEERING_LEVEL;
    }

    isMaxLeft(): boolean {
        return this.level <= -this.maxLevel;
    }

    isMaxRight(): boolean {
        return this.level >= this.maxLevel;
    }

    stepLeft(): void {
        if (!this.isMaxLeft())
            this.level -= 1;
    }

    stepRight(): void {
        if (!this.isMaxRight())
            this.level += 1;
    }

    applyInput(turnRight: boolean, turnLeft: boolean): void {
        this.pendingTurnMatrix = undefined;

        if (turnRight && turnLeft) {
            return;
        }

        if (!turnRight && !turnLeft) {
            if (this.isLeft()) {
                this.stepRight();
                this.pendingTurnMatrix = this.downMat;
            } else if (this.isRight()) {
                this.stepLeft();
                this.pendingTurnMatrix = this.upMat;
            }
        }

        if (turnRight && !this.isMaxRight()) {
            this.stepRight();
            this.pendingTurnMatrix = this.downMat;
        }

        if (turnLeft && !this.isMaxLeft()) {
            this.stepLeft();
            this.pendingTurnMatrix = this.upMat;
        }
    }

    currentTurnMatrix(): matrix | undefined {
        const turnMatrix = this.pendingTurnMatrix;
        this.pendingTurnMatrix = undefined;
        return turnMatrix;
    }

    reset(): void {
        this.level = STRAIGHT_STEERING_LEVEL;
    }

    setMaxLeft(): void {
        this.level = -this.maxLevel;
    }

    setMaxRight(): void {
        this.level = this.maxLevel;
    }
}
